import { useMemo } from 'react';
import { FilterFn, Row } from '@tanstack/react-table';
import moment from 'moment';

import {
  ExplorerItem,
  NormalizedAgreement,
  NormalizedFile,
} from '@/types/file-explorer-types';

import IconFolder from '@/assets/svg/icon-folder.svg?react';

import { cn } from '@/lib/utils';

import { useIsMobile } from '@/hooks/use-mobile';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useGlobalStore } from '@/stores/globalStore';

export enum ExplorerItemType {
  DIRECTORY,
  INSURANCE_OBJECT,
  INSURANCE_AREA,
  AGREEMENT,
  FILE,
}

export const dateFilterFn: FilterFn<any> = (row, columnId, filterValue) => {
  // If no filter value, return all rows
  if (!filterValue) return true;

  // Get cell value
  const cellValue = row.getValue(columnId);

  // If cell doesn't have a value, don't match
  if (!cellValue) return false;

  try {
    // Handle both Date objects and string dates
    let cellDate: Date;
    let filterDate: Date;

    // Parse cell value depending on its type
    if (cellValue instanceof Date) {
      cellDate = cellValue;
    } else if (typeof cellValue === 'string') {
      // If it's in DD.MM.YYYY format, parse with explicit format
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(cellValue)) {
        cellDate = moment(cellValue, 'DD.MM.YYYY').toDate();
      } else {
        cellDate = moment(cellValue).toDate();
      }
    } else {
      return false;
    }

    // Parse filter value depending on its type
    if (filterValue instanceof Date) {
      filterDate = filterValue;
    } else if (typeof filterValue === 'string') {
      filterDate = moment(filterValue, 'DD.MM.YYYY').toDate();
    } else {
      return false;
    }

    // Check if dates are valid
    if (isNaN(cellDate.getTime()) || isNaN(filterDate.getTime())) {
      return false;
    }

    // Format both dates to YYYY-MM-DD for comparison
    const cellDateStr = moment(cellDate).format('YYYY-MM-DD');
    const filterDateStr = moment(filterDate).format('YYYY-MM-DD');

    // Compare the dates
    return cellDateStr === filterDateStr;
  } catch (e) {
    // If there's any error in date parsing, don't match
    return false;
  }
};

export const COLUMN_NAMES = (type: ExplorerItemType) =>
  ({
    insurance_object: 'Дирекція',
    name: type === ExplorerItemType.AGREEMENT ? 'Документ' : 'Назва',
    created: 'Створено',
    modified: 'Змінено',
    agreement_number: 'Номер договору',
    index: 'Індекс',
    registration_plate: 'ДНЗ',
    insurance: 'Страхова компанія',
    file_type: 'Тип файлу',
    format: 'Формат',
    size: 'Розмір',
    height: 'Висота',
    upload_date: 'Дата завантаження',
    gps_latitude: 'GPS широта',
    gps_longitude: 'GPS довгота',
    user: 'Користувач',
    description: 'Опис',
    file_link: 'Посилання на файл',
    actions: 'Дія',
    step: 'Статус',
    assignee: 'Виконавець',
    creator: 'Автор',
  }) as const;

export type ColumnNamesType = keyof ReturnType<typeof COLUMN_NAMES>;

export const COLUMNS_BY_TYPE_OF_EXPLORER_ITEM = {
  [ExplorerItemType.DIRECTORY]: ['name'],
  [ExplorerItemType.INSURANCE_OBJECT]: ['name', 'created', 'modified'],
  [ExplorerItemType.AGREEMENT]: [
    'insurance_object',
    'name',
    'registration_plate',
    'index',
    'step',
    'assignee',
    'creator',
    'created',
    'modified',
  ] as (keyof NormalizedAgreement)[],
  [ExplorerItemType.INSURANCE_AREA]: ['name', 'created', 'modified'],
  [ExplorerItemType.FILE]: [
    'name',
    'agreement',
    'file_type',
    'format',
    'size',
    'height',
    'upload_date',
    'gps_latitude',
    'gps_longitude',
    'user',
    'description',
    'file_link',
    'created',
    'modified',
  ],
};

export const getColumnsConfig = (type: ExplorerItemType) => {
  const directoryColumns = COLUMNS_BY_TYPE_OF_EXPLORER_ITEM[type];
  const isMobile = useIsMobile();
  const { steps, users, indexes } = useGlobalStore();
  const { insuranceObjects, directories } = useApiManagerStore();
  const memoSteps = useMemo(
    () => (steps ?? []).filter((step) => !step.is_deleted),
    [steps],
  );
  const memoUsers = useMemo(() => users ?? [], [users]);
  const memoIndexes = useMemo(
    () => (indexes ?? []).filter((index) => !index.is_deleted),
    [indexes],
  );
  const memoInsuranceObjects = useMemo(
    () => insuranceObjects,
    [insuranceObjects],
  );
  const memoDirectories = useMemo(() => directories, [directories]);

  if (isMobile) {
    return directoryColumns
      .filter(
        (column) =>
          column === 'name' ||
          type === ExplorerItemType.AGREEMENT ||
          column === 'actions',
      )
      .map((column) => {
        switch (column) {
          case 'name':
            return {
              accessorKey: 'name',
              filterFn: 'exact' as const,
              header: () => (
                <div
                  className={cn(
                    'flex items-center gap-3 font-semibold',
                    'w-full',
                  )}
                >
                  {type === ExplorerItemType.AGREEMENT ? 'Документ' : 'Назва'}
                </div>
              ),
              cell: ({ row }: { row: Row<ExplorerItem> }) => (
                <div className={cn('flex items-center gap-3', 'w-full')}>
                  <span className="text-base font-medium">
                    {row.original.name}
                  </span>
                </div>
              ),
            };

          case 'insurance_object':
            return {
              accessorKey: 'insurance_object',
              filterFn: 'exact' as const,
              header: () => (
                <div
                  className={cn(
                    'flex items-center gap-3 font-semibold',
                    'w-full',
                  )}
                >
                  Дирекція
                </div>
              ),
              cell: ({ row }: { row: Row<ExplorerItem> }) => (
                <div className={cn('flex items-center gap-3', 'w-full')}>
                  <span className="text-base font-medium">
                    {
                      memoDirectories.find(
                        (dir) =>
                          memoInsuranceObjects.find(
                            (io) =>
                              io.uuid ===
                              (row.original as NormalizedAgreement)
                                .insurance_object,
                          )?.directory === dir.uuid,
                      )?.abbreviation
                    }
                  </span>
                </div>
              ),
            };

          case 'assignee':
            return {
              accessorKey: 'assignee',
              filterFn: 'exact' as const,
              header: () => (
                <div className="whitespace-nowrap font-semibold">
                  {COLUMN_NAMES(type)[column as ColumnNamesType]}
                </div>
              ),
              cell: ({ row }: { row: Row<NormalizedAgreement> }) => {
                const user = memoUsers.find(
                  (user) => user.uuid === row.original.assignee,
                );
                return (
                  <div className="whitespace-nowrap text-base font-medium">
                    {user ? `${user.first_name} ${user.last_name}` : ''}
                  </div>
                );
              },
            };
          case 'creator':
            return {
              accessorKey: 'creator',
              filterFn: 'exact' as const,
              header: () => (
                <div className="whitespace-nowrap font-semibold">
                  {COLUMN_NAMES(type)[column as ColumnNamesType]}
                </div>
              ),
              cell: ({ row }: { row: Row<NormalizedAgreement> }) => {
                const user = memoUsers.find(
                  (user) => user.uuid === row.original.creator,
                );
                return (
                  <div className="whitespace-nowrap text-base font-medium">
                    {user ? `${user.first_name} ${user.last_name}` : ''}
                  </div>
                );
              },
            };

          case 'index':
            return {
              accessorKey: 'index',
              filterFn: 'exact' as const,
              header: () => (
                <div className="whitespace-nowrap font-semibold">
                  {COLUMN_NAMES(type)[column as ColumnNamesType]}
                </div>
              ),
              cell: ({ row }: { row: Row<NormalizedAgreement> }) => {
                return (
                  <div className="whitespace-nowrap text-base font-medium">
                    {
                      memoIndexes.find(
                        (index) => index.uuid === row.original.index,
                      )?.name
                    }
                  </div>
                );
              },
            };

          default:
            return {
              accessorKey: column,
              filterFn: 'exact' as const,
              header: () => (
                <div className="whitespace-nowrap font-semibold">
                  {COLUMN_NAMES(type)[column as ColumnNamesType]}
                </div>
              ),
              cell: ({ row }: { row: Row<ExplorerItem> }) => (
                <span className="whitespace-nowrap text-base font-medium">
                  {row.original[column as keyof ExplorerItem] ?? ''}
                </span>
              ),
            };
        }
      });
  }

  return directoryColumns.map((column) => {
    switch (column) {
      case 'insurance_object':
        return {
          accessorKey: 'insurance_object',
          filterFn: 'exact' as const,
          header: () => (
            <div
              className={cn('flex items-center gap-3 font-semibold', 'w-full')}
            >
              {COLUMN_NAMES(type)[column as ColumnNamesType]}
            </div>
          ),
          cell: ({ row }: { row: Row<ExplorerItem> }) => (
            <div className={cn('flex items-center gap-3', 'w-full')}>
              <span className="text-base font-medium">
                {
                  memoDirectories.find(
                    (dir) =>
                      memoInsuranceObjects.find(
                        (io) =>
                          io.uuid ===
                          (row.original as NormalizedAgreement)
                            .insurance_object,
                      )?.directory === dir.uuid,
                  )?.abbreviation
                }
              </span>
            </div>
          ),
        };

      case 'name':
        return {
          accessorKey: 'name',
          filterFn: 'exact' as const,
          header: () => (
            <div
              className={cn('flex items-center gap-3 font-semibold', 'w-full')}
            >
              {COLUMN_NAMES(type)[column as ColumnNamesType]}
            </div>
          ),
          cell: ({ row }: { row: Row<ExplorerItem> }) => (
            <div className={cn('flex items-center gap-3', 'w-full')}>
              {type !== ExplorerItemType.AGREEMENT && (
                <IconFolder className="h-[22px] min-w-[30px]" />
              )}
              <span className="text-base font-medium">{row.original.name}</span>
            </div>
          ),
        };

      case 'created':
        return {
          accessorKey: 'created',
          filterFn: dateFilterFn,
          header: () => (
            <div className="whitespace-nowrap px-2 font-semibold">
              {COLUMN_NAMES(type)[column as ColumnNamesType]}
            </div>
          ),
          cell: ({ row }: { row: Row<ExplorerItem> }) => {
            const dateValue = row.original.created;
            // If it's already in DD.MM.YYYY format, return as is
            if (
              typeof dateValue === 'string' &&
              /^\d{2}\.\d{2}\.\d{4}$/.test(dateValue)
            ) {
              return (
                <span className="whitespace-nowrap px-2 text-base font-medium">
                  {dateValue}
                </span>
              );
            }
            // Otherwise, parse and format
            return (
              <span className="whitespace-nowrap px-2 text-base font-medium">
                {moment(dateValue).format('DD.MM.YYYY')}
              </span>
            );
          },
        };
      case 'modified':
        return {
          accessorKey: 'modified',
          filterFn: dateFilterFn,
          header: () => (
            <div className="whitespace-nowrap font-semibold">
              {COLUMN_NAMES(type)[column as ColumnNamesType]}
            </div>
          ),
          cell: ({ row }: { row: Row<ExplorerItem> }) => {
            const dateValue = row.original.modified;
            // If it's already in DD.MM.YYYY format, return as is
            if (
              typeof dateValue === 'string' &&
              /^\d{2}\.\d{2}\.\d{4}$/.test(dateValue)
            ) {
              return (
                <div className="whitespace-nowrap text-base font-medium">
                  {dateValue}
                </div>
              );
            }
            // Otherwise, parse and format
            return (
              <div className="whitespace-nowrap text-base font-medium">
                {moment(dateValue).format('DD.MM.YYYY')}
              </div>
            );
          },
        };

      case 'size':
        return {
          accessorKey: 'size',
          filterFn: 'exact' as const,
          header: () => (
            <div className="whitespace-nowrap text-center font-semibold">
              {COLUMN_NAMES(type)[column as ColumnNamesType]}
            </div>
          ),
          cell: ({ row }: { row: Row<ExplorerItem> }) => (
            <div className="whitespace-nowrap text-center text-base font-medium">
              {(row.original as NormalizedFile).size}
              MB
            </div>
          ),
        };

      case 'step':
        return {
          accessorKey: 'step',
          filterFn: 'exact' as const,
          header: () => (
            <div className="whitespace-nowrap font-semibold">
              {COLUMN_NAMES(type)[column as ColumnNamesType]}
            </div>
          ),
          cell: ({ row }: { row: Row<NormalizedAgreement> }) => {
            return (
              <div className="whitespace-nowrap text-base font-medium">
                {
                  memoSteps.find((step) => step.uuid === row.original.step)
                    ?.status_name
                }
              </div>
            );
          },
        };

      case 'assignee':
        return {
          accessorKey: 'assignee',
          filterFn: 'exact' as const,
          header: () => (
            <div className="whitespace-nowrap font-semibold">
              {COLUMN_NAMES(type)[column as ColumnNamesType]}
            </div>
          ),
          cell: ({ row }: { row: Row<NormalizedAgreement> }) => {
            const user = memoUsers.find((user) => user.uuid === row.original.assignee);
            return (
              <div className="whitespace-nowrap text-base font-medium">
                {user ? `${user.first_name} ${user.last_name}` : ''}
              </div>
            );
          },
        };
      case 'creator':
        return {
          accessorKey: 'creator',
          filterFn: 'exact' as const,
          header: () => (
            <div className="whitespace-nowrap font-semibold">
              {COLUMN_NAMES(type)[column as ColumnNamesType]}
            </div>
          ),
          cell: ({ row }: { row: Row<NormalizedAgreement> }) => {
            const user = memoUsers.find((user) => user.uuid === row.original.creator);
            return (
              <div className="whitespace-nowrap text-base font-medium">
                {user ? `${user.first_name} ${user.last_name}` : ''}
              </div>
            );
          },
        };

      case 'index':
        return {
          accessorKey: 'index',
          filterFn: 'exact' as const,
          header: () => (
            <div className="whitespace-nowrap font-semibold">
              {COLUMN_NAMES(type)[column as ColumnNamesType]}
            </div>
          ),
          cell: ({ row }: { row: Row<NormalizedAgreement> }) => {
            return (
              <div className="whitespace-nowrap text-base font-medium">
                {
                  memoIndexes.find((index) => index.uuid === row.original.index)
                    ?.name
                }
              </div>
            );
          },
        };

      default:
        return {
          accessorKey: column,
          filterFn: 'exact' as const,
          header: () => (
            <div className="whitespace-nowrap font-semibold">
              {COLUMN_NAMES(type)[column as ColumnNamesType]}
            </div>
          ),
          cell: ({ row }: { row: Row<ExplorerItem> }) => (
            <span className="whitespace-nowrap text-base font-medium">
              {row.original[column as keyof ExplorerItem] ?? ''}
            </span>
          ),
        };
    }
  });
};
